import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, PRIMARY_OUTLET, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { IUserInformation } from '../interfaces/userInformation';
import { selectUserInformation } from '../state/selectors/user-profile.selectors';
import { AppSettings } from '../shared/Constants/sharedConstants';

/**
 * Used when the visit is unthemed, or the theme has not named a product. Every themed organization
 * gets its own name from the theme instead - the same one their emails already carry.
 */
const DEFAULT_BRAND = 'SmartTank';

/**
 * Composes the browser tab title from two independent, per-route pieces instead of Angular's
 * default (which only takes the deepest route's `title` and does not translate it):
 *
 *  - `data.titleKey`: a translation key for the current section, e.g. "Details" or "Setup".
 *    Declared on the leaf route. Translated with `translate.instant` - safe to call
 *    synchronously here because every resource group is preloaded once at boot
 *    (see utilities/translation-groups.ts) before the router ever navigates.
 *  - `title`: an Angular Router `Resolve<string>` on a parameterized ancestor route (e.g.
 *    tank/:id), already resolved by Angular into the entity's display name (tank name, org
 *    name, etc.) by the time updateTitle runs. Not translated - it's a proper noun.
 *
 * Both can be declared at different depths of the same activated route tree; whichever one a
 * given route in the chain does not declare simply carries forward from its ancestor.
 *
 * A third piece follows both when it applies: the selected organization.
 *
 * Organizations are scoped per TAB - each tab carries its own on its own credential, and two tabs
 * can sit in two organizations indefinitely, including across a reload. Naming it makes a tab, a
 * bookmark and a pasted link say which organization they belong to.
 *
 * AFTER the page, not before it. Switching organization is mostly a customer-support activity, so it
 * is not what most people are scanning the tab strip for - the page they are on is. The trade-off is
 * real and accepted: browsers truncate from the right, so in a crowded strip the organization is the
 * first thing to disappear. It is context, not identity.
 *
 * Only when it is NOT the user's home organization. Someone who never switches - most users - sees
 * the title exactly as it was, with no noise; someone who has switched sees it precisely when it
 * carries information. Two tabs both on the home organization show no label and need none: they are
 * in the same place.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private organizationLabel: string | null = null;

  /**
   * The last route the title was composed for, so an organization change can re-compose it.
   *
   * Without this the label is only correct as of the last navigation. Switching organization does
   * not always navigate - the picker only redirects from routes that declare `csoRedirect` - so the
   * tab would keep announcing the organization it used to be in. A title that is sometimes wrong is
   * worse than no title, because it is read at a glance and trusted.
   */
  private lastSnapshot: RouterStateSnapshot | null = null;

  constructor(
    private readonly title: Title,
    private readonly translate: TranslateService,
    store: Store<AppState>,
  ) {
    super();

    // The store rather than AuthService: the Router constructs this strategy and AuthService depends
    // on the Router, so injecting it here would close a dependency cycle.
    store.select(selectUserInformation).subscribe((user) => {
      const label = this.labelForOrganization(user);
      if (label === this.organizationLabel) return;

      this.organizationLabel = label;
      if (this.lastSnapshot) this.updateTitle(this.lastSnapshot);
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.lastSnapshot = snapshot;

    const parts = [
      this.buildComposedTitle(snapshot),
      this.organizationLabel,
      this.brand,
    ].filter((part): part is string => !!part);

    this.title.setTitle(parts.join(' \u00b7 '));
  }

  /**
   * What this deployment calls itself: the theme's product name, or the default brand when the visit
   * is unthemed.
   *
   * Read on each composition rather than captured once, so it is right even if the theme resolves
   * after this service is constructed. It is a plain object read, not a request.
   */
  private get brand(): string {
    return AppSettings?.UIAppConfig?.currentTheme?.productName || DEFAULT_BRAND;
  }

  /**
   * The organization to announce, or null when there is nothing worth saying.
   *
   * Null for the user's home organization - that is the absence of a switch, not a switch to
   * somewhere. Null too when the name has not loaded yet, rather than showing a bare id: a number in
   * the tab strip is noise to everyone who has to read it.
   */
  private labelForOrganization(user: IUserInformation | null): string | null {
    if (!user?.lastSelectedOrganizationName) return null;

    return user.lastSelectedOrganizationId === user.organizationid
      ? null
      : user.lastSelectedOrganizationName;
  }

  private buildComposedTitle(snapshot: RouterStateSnapshot): string | null {
    let route: ActivatedRouteSnapshot | undefined = snapshot.root;
    let sectionLabel: string | null = null;
    let entityName: string | null = null;

    while (route) {
      const titleKey = route.data?.['titleKey'] as string | undefined;
      if (titleKey) {
        sectionLabel = this.translate.instant(titleKey);
      }
      if (route.title) {
        entityName = route.title;
      }

      // Aux outlets (e.g. the 'pageToolbar' tab-nav outlet under /user) never contribute to
      // the document title - only follow the primary outlet chain.
      route = route.children.find((child) => child.outlet === PRIMARY_OUTLET);
    }

    if (!sectionLabel) {
      return entityName;
    }
    return entityName ? `${entityName} \u00b7 ${sectionLabel}` : sectionLabel;
  }
}
